<template>
  <div class="mv-root">
    <!-- ═══ TELA: LISTA ═══ -->
    <template v-if="!openId">
      <header class="mv-header">
        <img :src="logoUrl" class="mv-logo" alt="ISBET" />
        <div class="mv-header-info">
          <span class="mv-header-name">{{ authUser?.name }}</span>
          <span class="mv-header-role">{{ myCount }} conversa{{ myCount === 1 ? '' : 's' }} com você</span>
        </div>
        <button class="mv-icon-btn" title="Sair" @click="handleLogout">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
        </button>
      </header>

      <div v-if="dataStale" class="mv-stale-bar">
        Sem conexão — dados desatualizados
        <button class="mv-stale-btn" @click="restart">Tentar</button>
      </div>

      <div class="mv-search-wrap">
        <svg class="mv-search-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input v-model="query" class="mv-search" type="search" placeholder="Buscar por nome ou telefone…" />
      </div>

      <nav class="mv-tabs">
        <button v-for="t in tabs" :key="t.key" class="mv-tab" :class="{ active: tab === t.key }" @click="tab = t.key">
          {{ t.label }} <span class="mv-tab-count">{{ t.count }}</span>
        </button>
      </nav>

      <div class="mv-list">
        <div v-if="!scopeReady" class="mv-state"><span class="mv-spinner"></span></div>
        <div v-else-if="!visible.length" class="mv-state">
          {{ query ? 'Nenhuma conversa encontrada.' : tab === 'mine' ? 'Nenhuma conversa ativa com você.' : 'Nada por aqui.' }}
        </div>
        <button v-for="c in visible" :key="c.id" class="mv-item" @click="openChat(c.id)">
          <span class="mv-av" :style="{ background: avatarColor(c.contact.name) }">
            <img v-if="c.contact.thumbnail" :src="c.contact.thumbnail" alt="" @error="(e) => (e.target as HTMLImageElement).style.display = 'none'" />
            <span v-else>{{ initials(c.contact.name) }}</span>
            <span class="mv-dot" :class="c.status"></span>
          </span>
          <span class="mv-item-body">
            <span class="mv-item-top">
              <span class="mv-item-name">{{ c.contact.name || fmtPhone(c.contact.phone) }}</span>
              <span v-if="c.waiting_since" class="mv-wait" :class="{ late: nowSec - c.waiting_since > 15 * 60 }">{{ waitLabel(c.waiting_since) }}</span>
            </span>
            <span class="mv-item-sub">
              <template v-if="c.fluxo_id">{{ c.fluxo_id }}</template>
              <template v-if="c.team"> · {{ c.team.name }}</template>
              <template v-if="tab !== 'mine'"> · {{ c.assignee?.name || 'Sem agente' }}</template>
            </span>
            <span v-if="c.labels.length" class="mv-item-tags">
              <span v-for="l in c.labels.slice(0, 3)" :key="l" class="mv-tag">{{ l }}</span>
            </span>
          </span>
        </button>
      </div>
    </template>

    <!-- ═══ TELA: CHAT ═══ -->
    <FocoChatPanel v-else :conversation-id="openId" mobile class="mv-chat" @close="closeChat" />
  </div>
</template>

<script setup lang="ts">
// Tela de celular no WootDash (substitui a antiga, que buscava conversas/mensagens/macros
// no n8n). Lista = os mesmos dados ao vivo do Dashboard (useWootDashLiveData: SignalR +
// lista negra + fluxos escondidos); conversa = o chat do Modo Foco (FocoChatPanel) em tela cheia.
// Avisos de atribuição e em massa já vêm do App.vue (SignalR), iguais ao desktop.
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import FocoChatPanel from '@/components/dashboard/FocoChatPanel.vue'
import { useWootDashLiveData, startWootDashLiveData, stopWootDashLiveData } from '@/composables/useWootDashLiveData'
import { pendingChatId, currentOpenConvId } from '@/composables/useChatTrigger'
import { avatarColor, initials } from '@/composables/useHelpers'
import { fmtPhone } from '@/utils/contactsFormat'
import { useLogo } from '@/composables/useLogo'
import { performLogout } from '@/composables/useAutoLogout'

const store = useStore()
const router = useRouter()
const { logoUrl } = useLogo()
const authUser = computed(() => store.getters.authUser)
const token = computed<string>(() => authUser.value?.wootdash_token ?? '')
const myAgentId = computed<string>(() => authUser.value?.wootdash_agent_id ?? '')

const { conversations, scopeReady, dataStale } = useWootDashLiveData()

type Tab = 'mine' | 'queue' | 'all'
const tab = ref<Tab>('mine')
const query = ref('')
const openId = ref<string | null>(null)
const nowSec = ref(Math.floor(Date.now() / 1000))

const mine = computed(() => conversations.value.filter(c => c.assignee?.id === myAgentId.value))
const queue = computed(() => conversations.value.filter(c => !c.assignee))
const myCount = computed(() => mine.value.length)

const tabs = computed(() => [
  { key: 'mine' as Tab, label: 'Minhas', count: mine.value.length },
  { key: 'queue' as Tab, label: 'Sem agente', count: queue.value.length },
  { key: 'all' as Tab, label: 'Todas', count: conversations.value.length },
])

const visible = computed(() => {
  const base = tab.value === 'mine' ? mine.value : tab.value === 'queue' ? queue.value : conversations.value
  const q = query.value.trim().toLowerCase()
  const digits = q.replace(/\D/g, '')
  const filtered = q
    ? base.filter(c => c.contact.name.toLowerCase().includes(q) || (digits.length >= 3 && c.contact.phone.replace(/\D/g, '').includes(digits)))
    : base
  // Quem espera há mais tempo primeiro; sem espera, mais recentes depois.
  return [...filtered].sort((a, b) => (a.waiting_since || Infinity) - (b.waiting_since || Infinity) || b.created_at - a.created_at)
})

function waitLabel(since: number): string {
  const min = Math.max(0, Math.floor((nowSec.value - since) / 60))
  if (min < 1) return 'agora'
  if (min < 60) return `${min} min`
  const h = Math.floor(min / 60)
  return h < 24 ? `${h} h` : `${Math.floor(h / 24)} d`
}

function openChat(id: string) {
  openId.value = id
  currentOpenConvId.value = id
}

function closeChat() {
  openId.value = null
  currentOpenConvId.value = null
}

// "Abrir" no aviso de atribuição (ou notificação) — mesmo sinal que o ConvDrawer escuta no desktop.
watch(pendingChatId, id => {
  if (id === null) return
  openChat(String(id))
  pendingChatId.value = null
}, { immediate: true })

function restart() {
  if (token.value) startWootDashLiveData(token.value)
}

async function handleLogout() {
  await performLogout(store, router)
}

let nowTimer: ReturnType<typeof setInterval> | null = null
onMounted(() => {
  restart()
  nowTimer = setInterval(() => { nowSec.value = Math.floor(Date.now() / 1000) }, 30_000)
})
onUnmounted(() => {
  stopWootDashLiveData()
  if (nowTimer) clearInterval(nowTimer)
  currentOpenConvId.value = null
})
watch(token, t => { if (t) restart() })
</script>

<style scoped>
.mv-root { height: 100dvh; display: flex; flex-direction: column; background: var(--ink-02); overflow: hidden; }
.mv-header { display: flex; align-items: center; gap: 10px; padding: calc(10px + env(safe-area-inset-top, 0px)) 16px 10px; background: var(--white); border-bottom: 1px solid var(--ink-10); flex-shrink: 0; }
.mv-logo { height: 28px; width: auto; }
.mv-header-info { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.mv-header-name { font-size: 14px; font-weight: 700; color: var(--ink-80); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.mv-header-role { font-size: 11px; color: var(--ink-40); }
.mv-icon-btn { width: 36px; height: 36px; border-radius: 50%; border: none; background: var(--ink-05); color: var(--ink-60); display: flex; align-items: center; justify-content: center; }

.mv-stale-bar { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 8px 16px; background: #fef3c7; color: #92400e; font-size: 12px; font-weight: 600; }
.mv-stale-btn { border: none; background: #92400e; color: #fff; border-radius: 12px; padding: 4px 12px; font-size: 12px; }

.mv-search-wrap { position: relative; padding: 10px 16px 6px; flex-shrink: 0; }
.mv-search-ico { position: absolute; left: 28px; top: 50%; width: 16px; height: 16px; transform: translateY(-30%); color: var(--ink-40); }
.mv-search { width: 100%; box-sizing: border-box; padding: 10px 12px 10px 36px; border: 1px solid var(--ink-10); border-radius: 10px; font-size: 15px; background: var(--white); outline: none; }
.mv-search:focus { border-color: var(--red); }

.mv-tabs { display: flex; gap: 6px; padding: 4px 16px 8px; flex-shrink: 0; overflow-x: auto; }
.mv-tab { display: flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 999px; border: 1px solid var(--ink-10); background: var(--white); font-size: 13px; font-weight: 600; color: var(--ink-60); white-space: nowrap; }
.mv-tab.active { background: var(--red); border-color: var(--red); color: #fff; }
.mv-tab-count { font-size: 11px; opacity: .8; font-variant-numeric: tabular-nums; }

.mv-list { flex: 1; overflow-y: auto; padding: 0 12px calc(12px + env(safe-area-inset-bottom, 0px)); display: flex; flex-direction: column; gap: 6px; }
.mv-state { display: flex; justify-content: center; padding: 40px 16px; color: var(--ink-40); font-size: 13px; text-align: center; }
.mv-spinner { width: 24px; height: 24px; border: 3px solid var(--ink-10); border-top-color: var(--red); border-radius: 50%; animation: mv-spin .8s linear infinite; }
@keyframes mv-spin { to { transform: rotate(360deg); } }

.mv-item { display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px; background: var(--white); border: 1px solid var(--ink-10); border-radius: 12px; text-align: left; width: 100%; }
.mv-item:active { background: var(--ink-05); }
.mv-av { position: relative; width: 42px; height: 42px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; color: #fff; font-weight: 700; font-size: 14px; }
.mv-av img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
.mv-dot { position: absolute; bottom: 0; right: 0; width: 11px; height: 11px; border-radius: 50%; border: 2px solid var(--white); }
.mv-dot.open { background: var(--blue); }
.mv-dot.pending { background: #d97706; }
.mv-item-body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.mv-item-top { display: flex; align-items: baseline; gap: 8px; }
.mv-item-name { flex: 1; min-width: 0; font-size: 14px; font-weight: 700; color: var(--ink-80); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.mv-wait { font-size: 11px; font-weight: 700; color: var(--ink-40); font-variant-numeric: tabular-nums; }
.mv-wait.late { color: #dc2626; }
.mv-item-sub { font-size: 12px; color: var(--ink-40); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.mv-item-tags { display: flex; gap: 4px; flex-wrap: wrap; margin-top: 2px; }
.mv-tag { font-size: 10px; font-weight: 600; padding: 1px 8px; border-radius: 10px; background: var(--ink-05); color: var(--ink-50); }

.mv-chat { flex: 1; min-height: 0; border-left: none; }
</style>
